import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, MenuItem, Select, FormControl, InputLabel, CircularProgress, Alert, Chip, Card, CardContent } from '@mui/material';
import { Download, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const API = '/api';

export default function CallReport() {
  const {user} = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    from: new Date(Date.now()-30*24*60*60*1000).toISOString().slice(0,10),
    to: new Date().toISOString().slice(0,10),
    telecaller: '',
    outcome: ''
  });

  const hdr = {headers:{Authorization:'Bearer '+localStorage.getItem('token')}};

  const load = async () => {
    try {
      setLoading(true); setError('');
      const [cr, ur] = await Promise.all([
        axios.get(API+'/calls?limit=5000', hdr),
        axios.get(API+'/users/team', hdr)
      ]);
      setData(cr.data.calls||[]);
      setUsers(ur.data.users||[]);
    } catch(e) { setError(e.response?.data?.message||'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(()=>{load();},[]);

  const filtered = data.filter(c=>{
    if(!c.callTime) return false;
    const d = new Date(c.callTime);
    const from = new Date(filters.from+'T00:00:00');
    const to = new Date(filters.to+'T23:59:59');
    const matchDate = !filters.from || (d>=from && d<=to);
    const matchUser = !filters.telecaller || c.calledBy?._id===filters.telecaller;
    const matchOutcome = !filters.outcome || c.outcome===filters.outcome;
    return matchDate && matchUser && matchOutcome;
  });

  // Group by telecaller for summary
  const summary = {};
  filtered.forEach(c=>{
    const name = c.calledBy?.name || 'Unknown';
    if(!summary[name]) summary[name]={name,total:0,answered:0,notInterested:0,onHold:0,interested:0,noAnswer:0,busy:0,callback:0};
    summary[name].total++;
    if(c.outcome==='answered') summary[name].answered++;
    else if(c.outcome==='not-interested') summary[name].notInterested++;
    else if(c.outcome==='on-hold') summary[name].onHold++;
    else if(c.outcome==='interested') summary[name].interested++;
    else if(c.outcome==='no-answer') summary[name].noAnswer++;
    else if(c.outcome==='busy') summary[name].busy++;
    else if(c.outcome==='callback-requested') summary[name].callback++;
  });

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Employee Name','Total Calls','Answered','Not Interested','On Hold','Interested','No Answer','Busy','Callback'],
      ...Object.values(summary).map(r=>[r.name,r.total,r.answered,r.notInterested,r.onHold,r.interested,r.noAnswer,r.busy,r.callback])
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');

    // Detail sheet
    const detailData = [
      ['#','Date','Time','Employee Name','Customer Name','Phone','Outcome','Reason','Duration (sec)','Notes'],
      ...filtered.map((c,i)=>[
        i+1,
        new Date(c.callTime).toLocaleDateString('en-IN'),
        new Date(c.callTime).toLocaleTimeString('en-IN'),
        c.calledBy?.name||'',
        c.lead?.name||((c.lead?.firstName||'')+' '+(c.lead?.lastName||'')).trim()||'',
        c.lead?.phone||'',
        c.outcome?.replace(/-/g,' ')||'',
        c.reason||'',
        c.duration||0,
        c.notes||''
      ])
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(detailData), 'Call Details');

    XLSX.writeFile(wb, 'Call_Report_'+filters.from+'_to_'+filters.to+'.xlsx');
  };

  const OUTCOME_COLORS = {
    answered:'#4CAF50', 'not-interested':'#F44336', 'on-hold':'#FF9800',
    interested:'#2196F3', 'no-answer':'#9E9E9E', busy:'#FF5722', 'callback-requested':'#9C27B0'
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Call Report</Typography>
          <Typography variant="body2" color="text.secondary">{filtered.length} calls in selected period</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Refresh/>} onClick={load} sx={{borderColor:'#003e6f',color:'#003e6f'}}>Refresh</Button>
          <Button variant="contained" startIcon={<Download/>} onClick={exportExcel} sx={{bgcolor:'#003e6f','&:hover':{bgcolor:'#002a4d'}}}>Export Excel</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{mb:2}}><CardContent sx={{py:1.5,'&:last-child':{pb:1.5}}}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" label="From Date" type="date" value={filters.from}
            onChange={e=>setFilters({...filters,from:e.target.value})} InputLabelProps={{shrink:true}} sx={{minWidth:150}}/>
          <TextField size="small" label="To Date" type="date" value={filters.to}
            onChange={e=>setFilters({...filters,to:e.target.value})} InputLabelProps={{shrink:true}} sx={{minWidth:150}}/>
          <FormControl size="small" sx={{minWidth:160}}><InputLabel>Telecaller</InputLabel>
            <Select value={filters.telecaller} label="Telecaller" onChange={e=>setFilters({...filters,telecaller:e.target.value})}>
              <MenuItem value="">All</MenuItem>
              {users.filter(u=>u.role==='telecaller'||u.role==='admin').map(u=><MenuItem key={u._id} value={u._id}>{u.name||u.email}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{minWidth:160}}><InputLabel>Outcome</InputLabel>
            <Select value={filters.outcome} label="Outcome" onChange={e=>setFilters({...filters,outcome:e.target.value})}>
              <MenuItem value="">All</MenuItem>
              {['answered','not-interested','on-hold','interested','no-answer','busy','callback-requested'].map(o=><MenuItem key={o} value={o}>{o.replace(/-/g,' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <Button size="small" onClick={()=>setFilters({from:new Date().toISOString().slice(0,10),to:new Date().toISOString().slice(0,10),telecaller:'',outcome:''})}>Clear</Button>
        </Box>
      </CardContent></Card>

      {error&&<Alert severity="error" sx={{mb:2}}>{error}</Alert>}

      {/* Summary Cards */}
      {Object.keys(summary).length>0&&(
        <Box mb={2}>
          <Typography variant="h6" fontWeight={700} fontSize={14} mb={1}>Summary by Telecaller</Typography>
          <Box sx={{overflowX:'auto'}}>
            <Table size="small" component={Paper} sx={{minWidth:800,borderRadius:2,mb:1}}>
              <TableHead>
                <TableRow sx={{'& th':{bgcolor:'#003e6f',color:'white',fontWeight:600,fontSize:12,py:1.2}}}>
                  <TableCell>Employee Name</TableCell>
                  <TableCell align="center">Total Calls</TableCell>
                  <TableCell align="center">Answered</TableCell>
                  <TableCell align="center">Interested</TableCell>
                  <TableCell align="center">Not Interested</TableCell>
                  <TableCell align="center">On Hold</TableCell>
                  <TableCell align="center">No Answer</TableCell>
                  <TableCell align="center">Busy</TableCell>
                  <TableCell align="center">Callback</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.values(summary).map((r,i)=>(
                  <TableRow key={i} sx={{'&:hover':{bgcolor:'#f0f4ff'},bgcolor:i%2===0?'white':'#f9f9f9'}}>
                    <TableCell fontWeight={600}>{r.name}</TableCell>
                    <TableCell align="center"><Typography fontWeight={700} color="primary">{r.total}</Typography></TableCell>
                    <TableCell align="center"><Chip label={r.answered} size="small" sx={{bgcolor:'#4CAF5018',color:'#4CAF50',fontWeight:700,minWidth:32}}/></TableCell>
                    <TableCell align="center"><Chip label={r.interested} size="small" sx={{bgcolor:'#2196F318',color:'#2196F3',fontWeight:700,minWidth:32}}/></TableCell>
                    <TableCell align="center"><Chip label={r.notInterested} size="small" sx={{bgcolor:'#F4433618',color:'#F44336',fontWeight:700,minWidth:32}}/></TableCell>
                    <TableCell align="center"><Chip label={r.onHold} size="small" sx={{bgcolor:'#FF980018',color:'#FF9800',fontWeight:700,minWidth:32}}/></TableCell>
                    <TableCell align="center"><Chip label={r.noAnswer} size="small" sx={{bgcolor:'#9E9E9E18',color:'#9E9E9E',fontWeight:700,minWidth:32}}/></TableCell>
                    <TableCell align="center"><Chip label={r.busy} size="small" sx={{bgcolor:'#FF572218',color:'#FF5722',fontWeight:700,minWidth:32}}/></TableCell>
                    <TableCell align="center"><Chip label={r.callback} size="small" sx={{bgcolor:'#9C27B018',color:'#9C27B0',fontWeight:700,minWidth:32}}/></TableCell>
                  </TableRow>
                ))}
                {/* Total row */}
                <TableRow sx={{bgcolor:'#003e6f15'}}>
                  <TableCell sx={{fontWeight:700}}>TOTAL</TableCell>
                  <TableCell align="center" sx={{fontWeight:700,color:'#003e6f'}}>{filtered.length}</TableCell>
                  {['answered','interested','notInterested','onHold','noAnswer','busy','callback'].map(k=>(
                    <TableCell key={k} align="center" sx={{fontWeight:700}}>
                      {Object.values(summary).reduce((a,r)=>a+(r[k]||0),0)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Detailed Call Log */}
      <Typography variant="h6" fontWeight={700} fontSize={14} mb={1}>Detailed Call Log</Typography>
      {loading ? <CircularProgress sx={{color:'#003e6f'}}/> : (
        <TableContainer component={Paper} elevation={1} sx={{borderRadius:2}}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:600,fontSize:11,color:'#555',py:1.2}}}>
                <TableCell>#</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Customer Name</TableCell>
                <TableCell>Mobile No</TableCell>
                <TableCell>Calling Status</TableCell>
                <TableCell>Consumer Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length===0
                ?<TableRow><TableCell colSpan={9} align="center" sx={{py:5,color:'text.secondary'}}>No call records found for selected period</TableCell></TableRow>
                :filtered.map((c,i)=>(
                  <TableRow key={c._id} hover sx={{'&:hover':{bgcolor:'#f0f4ff'}}}>
                    <TableCell sx={{color:'#999',fontSize:11}}>{i+1}</TableCell>
                    <TableCell sx={{fontSize:11}}>
                      <Typography fontSize={11} fontWeight={600}>{new Date(c.callTime).toLocaleDateString('en-IN')}</Typography>
                      <Typography fontSize={10} color="text.secondary">{new Date(c.callTime).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</Typography>
                    </TableCell>
                    <TableCell sx={{fontSize:12,fontWeight:500}}>{c.calledBy?.name||'—'}</TableCell>
                    <TableCell sx={{fontSize:12}}>{c.lead?.name||((c.lead?.firstName||'')+' '+(c.lead?.lastName||'')).trim()||'—'}</TableCell>
                    <TableCell sx={{fontSize:12,fontFamily:'monospace'}}>{c.lead?.phone||'—'}</TableCell>
                    <TableCell>
                      <Chip label={['answered','no-answer','busy','voicemail'].includes(c.outcome)?c.outcome.replace(/-/g,' '):'Answered'}
                        size="small" sx={{fontSize:10,height:20,
                          bgcolor:['no-answer','busy'].includes(c.outcome)?'#FF980018':'#4CAF5018',
                          color:['no-answer','busy'].includes(c.outcome)?'#FF9800':'#4CAF50'}}/>
                    </TableCell>
                    <TableCell>
                      {['not-interested','on-hold','interested','callback-requested'].includes(c.outcome)&&(
                        <Chip label={c.outcome.replace(/-/g,' ')} size="small"
                          sx={{fontSize:10,height:20,bgcolor:(OUTCOME_COLORS[c.outcome]||'#9E9E9E')+'18',color:OUTCOME_COLORS[c.outcome]||'#9E9E9E'}}/>
                      )}
                    </TableCell>
                    <TableCell sx={{fontSize:11,color:'text.secondary'}}>{c.reason||'—'}</TableCell>
                    <TableCell sx={{fontSize:11,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.notes||'—'}</TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
